import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer } from '@angular/platform-browser';
import * as marked from 'marked';
import * as moment from 'moment';

import { AccountService } from '../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import {
  BookEventDirectlyResponse,
  ScheduledEventService
} from '../../../../../../services/scheduled-event/scheduled-event.service';
import {
  EventPricingMatrix,
  EventQuestion,
  NonRepeatingEvent
} from '../../../../../../models/event-type';
import { EventQuestionScope, EventQuestionType } from '../../../../../../models/event-type-enums';
import {
  PrivateEventBooking,
  PrivateEventBookingReservation
} from '../../../../../../models/scheduled-event';
import { Customer, CustomerSearchResult } from '../../../../../../models/customer';
import { CallbackResult, CallbackResultStatus } from '../../../../../../utils';
import {
  CreateNewCustomerModalComponent
} from '../../../customers/modals/create-new-customer-modal/create-new-customer-modal.component';
import {
  SelectCustomerModalComponent
} from '../../../modals/select-customer-modal/select-customer-modal.component';
import {
  SimpleMessageModalComponent
} from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';

/**
 * The subset of customer fields the modal needs when a customer is selected
 * (satisfied by both CustomerSearchResult and Customer).
 */
interface SelectedCustomer {
  id: number;
  first_name: string;
  last_name: string;
  birthday: string;
  email: string;
}

@Component({
  selector: 'app-add-participant-to-private-event-modal',
  templateUrl: './add-participant-to-private-event-modal.component.html',
  styleUrls: ['./add-participant-to-private-event-modal.component.css']
})
export class AddParticipantToPrivateEventModalComponent implements OnInit {

  // The private event whose booking the participant is added to.
  @Input() privateEventId: number;
  // The current booking, used to find the first open slot and guard against
  // duplicate participants.
  @Input() booking: PrivateEventBooking;
  // When provided (opened from the Event Details page), the event part the
  // participant can be checked into immediately. Null when opened from the
  // edit-reservations page.
  @Input() eventPartId: number | null = null;
  // The start time of the event part (provided when opened from the Event
  // Details page), used to default and warn on the "check in immediately"
  // option.
  @Input() eventPartStartDatetime: string | null = null;
  // checkInReservationPartId is the reservation part the opener should check
  // the participant into immediately, or null (PRO-1220).
  @Input() participantAddedCallback: (result: CallbackResult,
                                      checkInReservationPartId: number | null) => void;

  public privateEvent: NonRepeatingEvent = null;
  public pricingMatrix: EventPricingMatrix = null;
  public participantQuestions: EventQuestion[] = [];

  // The new participant being added.
  public reservation: PrivateEventBookingReservation;
  // True when there is no open slot, so adding will increase the event's size.
  public willIncreaseEventSize = false;
  // Offered only when an event part id is provided (PRO-1221).
  public checkInImmediately = false;

  public loading = true;
  public loadingSubmit = false;
  public errorMessage = '';
  public serverError: number = null;

  public readonly EventQuestionType = EventQuestionType;

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private scheduledEventService: ScheduledEventService,
    private domSanitizer: DomSanitizer,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.initReservationFromFirstOpenSlot();

    // Mirrors CreateBookingModalComponent: default to checked when the event
    // part starts within the hour or has started (PRO-1221).
    if ((this.eventPartId !== null) && this.eventPartStartDatetime) {
      this.checkInImmediately = !this.startsMoreThanOneHourFromNow();
    }

    this.loadPrivateEvent();
  }

  /**
   * Whether the event part starts more than one hour from now. When true, the
   * "check in immediately" checkbox is not checked by default, and a warning is
   * shown if the user checks it anyway.
   */
  public startsMoreThanOneHourFromNow(): boolean {
    if (!this.eventPartStartDatetime) { return false; }
    return moment(this.eventPartStartDatetime).diff(moment(), 'hours', true) > 1;
  }

  /**
   * A human-readable description of how far in the future the event part starts,
   * e.g. "3 hours" or "2 days". Used in the warning shown when checking a
   * participant in well before the event starts.
   */
  public getTimeUntilEventDisplay(): string {
    const start = moment(this.eventPartStartDatetime);
    const hoursFromNow = start.diff(moment(), 'hours', true);

    if (hoursFromNow < 24) {
      const hours = Math.max(1, Math.round(hoursFromNow));
      return hours === 1 ? '1 hour' : hours + ' hours';
    }

    const days = Math.round(start.diff(moment(), 'days', true));
    return days === 1 ? '1 day' : days + ' days';
  }

  /**
   * Initialize the working reservation, defaulting fees/add-ons/question
   * responses from the first open slot when one exists. When none exists, flag
   * that adding the participant will increase the event's size.
   */
  private initReservationFromFirstOpenSlot() {
    const openSlot = this.booking.reservations.find(
      res => !res.customer_id && ((res.first_name + res.last_name).trim() === '')
    );

    this.willIncreaseEventSize = !openSlot;

    this.reservation = {
      first_name: '',
      last_name: '',
      birthday: '',
      email: '',
      customer_id: null,
      question_responses: openSlot ? { ...openSlot.question_responses } : {},
      payment_fee_name: openSlot ? openSlot.payment_fee_name : null,
      add_on_names: openSlot ? [...openSlot.add_on_names] : [],
      per_participant_base_fee: '0.00',
      per_participant_add_on_fee: '0.00',
      confirmation_email_sent: false,
      send_confirmation_email: false
    };
  }

  private loadPrivateEvent() {
    this.loading = true;

    this.eventTypeService
      .getNonRepeatingEvent(this.privateEventId)
      .then(privateEvent => {
        this.privateEvent = privateEvent;
        return Promise.all([
          this.loadPricingMatrix(),
          this.loadEventQuestions()
        ]);
      })
      .catch(reason => { this.handleLoadError(reason); })
      .finally(() => { this.loading = false; });
  }

  private loadPricingMatrix(): Promise<void> {
    if (!this.privateEvent.pricing_matrix_id) {
      this.pricingMatrix = null;
      return Promise.resolve();
    }

    return this.eventTypeService
      .getEventPricingMatrix(
        this.privateEvent.event_type_id,
        this.privateEvent.pricing_matrix_id
      )
      .then(matrix => {
        this.pricingMatrix = matrix;

        // Default the participant fee when no open slot prefilled one.
        const mustDefaultFee = (
          !this.reservation.payment_fee_name &&
          (this.pricingMatrix.per_participant_fees.length > 0)
        );
        if (mustDefaultFee) {
          this.reservation.payment_fee_name = this.pricingMatrix.per_participant_fees[0].name;
        }
      });
  }

  private loadEventQuestions(): Promise<void> {
    return this.eventTypeService
      .getEventQuestions(this.privateEvent.event_type_id, 1, 100)
      .then(response => {
        this.participantQuestions = response.results
          .filter(q => q.scope === EventQuestionScope.participant);
      });
  }

  private handleLoadError(reason: any) {
    if (reason.status === 403) {
      this.accountService.logout();
    } else {
      this.serverError = reason.status;
    }
  }

  public openSelectCustomer() {
    const modalRef = this.modalService.open(SelectCustomerModalComponent);
    modalRef.componentInstance.excludeCustomerIds = [];
    modalRef.componentInstance.submitCallback = (result: CustomerSearchResult) => {
      if (this.rejectIfAlreadyParticipant(result)) { return; }
      this.setReservationCustomer(result);
    };
  }

  public openCreateNewCustomer() {
    const modalRef = this.modalService.open(CreateNewCustomerModalComponent);
    modalRef.componentInstance.savedCallback = (result: Customer) => {
      if (this.rejectIfAlreadyParticipant(result)) { return; }
      this.setReservationCustomer(result);
    };
  }

  /**
   * Reject a customer who is already a participant on this private event,
   * showing the same error as the edit-reservations page. Returns true when the
   * selection was rejected.
   */
  private rejectIfAlreadyParticipant(result: SelectedCustomer): boolean {
    const alreadyParticipant = this.booking.reservations.some(
      res => res.customer_id === result.id
    );

    if (alreadyParticipant) {
      const errorModalRef = this.modalService.open(SimpleMessageModalComponent);
      errorModalRef.componentInstance.headerText = 'Customer Already Added';
      errorModalRef.componentInstance.messageText = (
        result.first_name + ' ' + result.last_name +
        ' is already a participant on this private event.'
      );
      errorModalRef.componentInstance.messageAlertClass = 'alert-danger';
      return true;
    }

    return false;
  }

  private setReservationCustomer(result: SelectedCustomer) {
    this.reservation.customer_id = result.id;
    this.reservation.first_name = result.first_name;
    this.reservation.last_name = result.last_name;
    this.reservation.birthday = result.birthday;
    this.reservation.email = result.email;
  }

  public clearReservationCustomer() {
    this.reservation.customer_id = null;
    this.reservation.first_name = '';
    this.reservation.last_name = '';
    this.reservation.birthday = '';
    this.reservation.email = '';
  }

  public toggleAddOn(addOnNames: string[], addOnName: string) {
    if (addOnNames.includes(addOnName)) {
      while (addOnNames.includes(addOnName)) {
        addOnNames.splice(addOnNames.indexOf(addOnName), 1);
      }
    } else {
      addOnNames.push(addOnName);
    }
  }

  public getRenderedInlineMarkdown(content: string) {
    return this.domSanitizer.bypassSecurityTrustHtml(
      marked.parseInline(content, { breaks: true })
    );
  }

  public singleCheckboxChecked(questionResponses: {[key: string]: any}, questionName: string): boolean {
    return questionResponses[questionName] === 'on';
  }

  public toggleSingleCheckbox(questionResponses: {[key: string]: any}, questionName: string) {
    if (this.singleCheckboxChecked(questionResponses, questionName)) {
      questionResponses[questionName] = null;
    } else {
      questionResponses[questionName] = 'on';
    }
  }

  public multiCheckboxChecked(questionResponses: {[key: string]: any}, questionName: string, option: string): boolean {
    const questionResponse = questionResponses[questionName];

    if (!questionResponse) {
      return false;
    }

    return questionResponse.includes(option);
  }

  public toggleMultiCheckbox(questionResponses: {[key: string]: any}, questionName: string, option: string) {
    if (!questionResponses[questionName]) {
      questionResponses[questionName] = [option];
      return;
    }

    const questionResponse: string[] = questionResponses[questionName];

    if (questionResponse.includes(option)) {
      questionResponse.splice(questionResponse.indexOf(option), 1);
    } else {
      questionResponse.push(option);
    }
  }

  public handleSubmit() {
    if (!this.reservation.customer_id) { return; }

    this.loadingSubmit = true;
    this.errorMessage = '';

    this.scheduledEventService
      .addParticipantToPrivateEvent(this.privateEventId, this.reservation)
      .then(response => {
        const checkInReservationPartId = this.checkInImmediately
          ? this.getReservationPartIdForEventPart(response)
          : null;

        this.participantAddedCallback(
          {
            text: (
              this.reservation.first_name + ' ' + this.reservation.last_name +
              ' was added to the private event.'
            ),
            status: CallbackResultStatus.success
          },
          checkInReservationPartId
        );
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          const message = (reason.error && reason.error.useful_error_message)
            ? reason.error.useful_error_message
            : 'Unable to add this participant.';
          const errorModalRef = this.modalService.open(SimpleMessageModalComponent);
          errorModalRef.componentInstance.headerText = 'Unable to Add Participant';
          errorModalRef.componentInstance.messageText = message;
          errorModalRef.componentInstance.messageAlertClass = 'alert-danger';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to add a participant.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  /**
   * Find the reservation part ID corresponding to the event part shown in this
   * modal, from the add-participant response. Returns null if no event part was
   * provided or no match is found.
   */
  private getReservationPartIdForEventPart(response: BookEventDirectlyResponse): number | null {
    if (this.eventPartId === null) { return null; }

    const match = response.reservation_parts.find(
      part => part.event_part_id === this.eventPartId
    );
    return match ? match.reservation_part_id : null;
  }
}
